import { useCallback } from 'react';
import LogicFlow from '@logicflow/core';

interface UseOPMHighlightProps {
  lfRef: React.RefObject<LogicFlow | null>;
}

export const useOPMHighlight = ({ lfRef }: UseOPMHighlightProps) => {
  // 添加高亮样式，隐藏NodeResize控制点
  const addHighlightStyles = useCallback(() => {
    // 移除旧样式
    const oldStyle = document.getElementById('opl-highlight-style');
    if (oldStyle) {
      oldStyle.remove();
    }

    // 创建新样式
    const style = document.createElement('style');
    style.id = 'opl-highlight-style';
    style.textContent = `
      /* 隐藏NodeResize的控制点和边框 */
      .lf-node-selected .lf-resize-outline,
      .lf-node-selected .lf-resize-control,
      .lf-resize-outline,
      .lf-resize-control {
        display: none !important;
        visibility: hidden !important;
      }

      /* 节点高亮样式 */
      .lf-node-selected rect,
      .lf-node-selected circle,
      .lf-node-selected ellipse,
      .lf-node-selected polygon,
      .lf-node-selected path {
        fill: #f5f5f5 !important;
        stroke: #d9d9d9 !important;
        stroke-width: 2 !important;
      }

      /* 边高亮样式 */
      .lf-edge-selected path,
      .lf-edge-selected line {
        stroke: #d9d9d9 !important;
        stroke-width: 3 !important;
      }

      /* 隐藏所有调整大小相关的元素 */
      g[class*="resize"] {
        display: none !important;
      }

      /* 隐藏选中时的默认边框 */
      .lf-node-selected .lf-node-outline {
        display: none !important;
      }
    `;

    document.head.appendChild(style);
    console.log('高亮样式已添加，NodeResize控制点已隐藏');
  }, []);

  // 清除高亮样式
  const removeHighlightStyles = useCallback(() => {
    const style = document.getElementById('opl-highlight-style');
    if (style) {
      style.remove();
    }
  }, []);

  // OPL 高亮元素
  const handleOPLHighlightElements = useCallback((elementIds: string[]) => {
    if (!lfRef.current) return;

    console.log('OPL 高亮元素:', elementIds);

    // 清除之前的选择
    lfRef.current.clearSelectElements();

    // 选择指定的元素
    elementIds.forEach(id => {
      lfRef.current?.selectElementById(id, true);
    });

    // 添加高亮样式
    addHighlightStyles();
  }, [lfRef, addHighlightStyles]);

  // OPL 聚焦元素
  const handleOPLFocusElements = useCallback((elementIds: string[]) => {
    if (!lfRef.current) return;

    console.log('OPL 聚焦元素:', elementIds);

    // 如果有元素，聚焦到第一个元素（优先找节点）
    if (elementIds.length > 0) {
      // 尝试找到第一个节点来聚焦
      for (const elementId of elementIds) {
        const nodeData = lfRef.current.getNodeModelById(elementId);
        if (nodeData) {
          lfRef.current.focusOn({
            id: elementId,
            coordinate: {
              x: nodeData.x,
              y: nodeData.y,
            },
          });
          break;
        }
      }
    }

    // 高亮相关元素
    handleOPLHighlightElements(elementIds);
  }, [lfRef, handleOPLHighlightElements]);

  // 清除OPL高亮
  const handleOPLClearHighlight = useCallback(() => {
    if (!lfRef.current) return;

    console.log('清除OPL高亮');

    // 清除选择
    lfRef.current.clearSelectElements();

    // 移除高亮样式
    removeHighlightStyles();
  }, [lfRef, removeHighlightStyles]);

  // 搜索聚焦节点
  const handleSearchFocusNode = useCallback((nodeId: string) => {
    if (!lfRef.current) return;

    console.log('搜索聚焦节点:', nodeId);

    // 清除之前的选择
    lfRef.current.clearSelectElements();

    // 选择并聚焦到节点
    lfRef.current.selectElementById(nodeId);

    const nodeData = lfRef.current.getNodeModelById(nodeId);
    if (nodeData) {
      lfRef.current.focusOn({
        id: nodeId,
        coordinate: {
          x: nodeData.x,
          y: nodeData.y,
        },
      });
    }
  }, [lfRef]);

  // 搜索高亮路径
  const handleSearchHighlightPath = useCallback((nodeIds: string[], edgeIds: string[]) => {
    if (!lfRef.current) return;

    console.log('搜索高亮路径:', { nodeIds, edgeIds });

    // 清除之前的选择
    lfRef.current.clearSelectElements();

    // 选择路径中的所有节点和边
    [...nodeIds, ...edgeIds].forEach(id => {
      lfRef.current?.selectElementById(id);
    });
  }, [lfRef]);

  return {
    addHighlightStyles,
    removeHighlightStyles,
    handleOPLHighlightElements,
    handleOPLFocusElements,
    handleOPLClearHighlight,
    handleSearchFocusNode,
    handleSearchHighlightPath
  };
};